/**
 *     ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { ActivatedRoute } from '@angular/router';
import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { DropdownModel, ErrorResponseModel } from '../../../../../../../core/models';
import { FormBuilder, Validators } from '@angular/forms';
import { GslbApiService } from '../../../../http';
import { GslbFrontendModel, GslbRealServiceModel, GslbTopologyModel, TemplateModel } from '../../../../models';
import { LanguageService, LogService, ModalService, UtilsService } from '../../../../../../../core/services';
import { LbErrorService, WizardService, WizardTemplateService } from '../../../../services';
import { ServiceBaseComponent } from '../../serviceBase.component';
import { WizardSubStepTitlesEnum, WizardTitleEnum } from '../../../../enums';
import { forkJoin } from 'rxjs';
import { ipValidator } from '../../../../../../../core/directives';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-topology',
    templateUrl: './topology.component.html'
})

export class TopologyComponent extends ServiceBaseComponent implements OnInit {
    /**
     * Store the edit id.
     */
    public editId: string;

    /**
     * Store the dropdown content for use in the view.
     */
    public dropdown: any = {
        members: []
    };

    /**
     * Our available made topologies from the API.
     */
    public topologies: GslbTopologyModel[] = [];

    /**
     * Our available made members from the API.
     */
    public members: GslbRealServiceModel[] = [];

    constructor(
        private gslbApiService: GslbApiService,
        cdr: ChangeDetectorRef,
        formBuilder: FormBuilder,
        languageService: LanguageService,
        lbErrorService: LbErrorService,
        logService: LogService,
        modalService: ModalService,
        route: ActivatedRoute,
        utilsService: UtilsService,
        wizardService: WizardService,
        wizardTemplateService: WizardTemplateService
    ) {
        super(
            cdr,
            formBuilder,
            languageService,
            lbErrorService,
            logService,
            modalService,
            route,
            utilsService,
            wizardService,
            wizardTemplateService
        );

        this.setupBaseSubscriber();
    }

    ngOnInit() {
        this.componentTitle = WizardTitleEnum.TOPOLOGIES;
        this.setupFormGroup();
        this.setupFormSubscribers();
        this.setupWizardSubscribers();
        this.initLoading();
    }

    /**
     * Set up the form for the page.
     * @private
     */
    private setupFormGroup(): void {
        this.serviceForm = this.formBuilder.group({
            [WizardSubStepTitlesEnum.GENERAL]: this.formBuilder.group({
                // General
                id: [''], // <- Used when editing.
                ip: [null, [Validators.required, ipValidator]],
                mask: [null, [Validators.required, Validators.min(0), Validators.max(128)]],
                member: [null, [Validators.required]]
            })
        });

        this.setupClassVariablesFromForm(WizardTitleEnum.TOPOLOGIES);
    }

    /**
     * Setup form subscribers for required inputs and inputs that change the steps in the wizard.
     * @private
     */
    private setupFormSubscribers(): void {
        this.serviceForm.valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: () => {
                    // Validate the form each step of the way. Use debounce to reduce the number of times the callback is called.
                    this.utilsService.debounce('topologies', () => {
                        if (this.isCurrentStepValid()) {
                            this.outputFormValues();
                        }
                    });
                }
            });
    }

    /**
     * Setup wizard subscribers.
     * @private
     */
    private setupWizardSubscribers(): void {
        this.wizardService.wizardModeEmitter
            .pipe(takeUntil(this.destroyed$))
            .subscribe(
                advancedState => {
                    this.advancedMode = advancedState;
                }
            );
    }

    /**
     * Loads both the floating IP groups and Interfaces and set the values.
     * @private
     */
    private initLoading(): void {
        forkJoin({
            members: this.gslbApiService.getRealServers(),
            topologies: this.gslbApiService.getTopologies(),
        })
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                    next: (dataGroup: any) => {
                        const serviceError = this.getErrorForService(WizardTitleEnum.TOPOLOGIES);
                        const template = this.getTemplate(WizardTitleEnum.TOPOLOGIES, serviceError);
                        this.editId = template?.id?.value && !template.id.value.includes('temp-') ? template.id.value : null;

                        if (dataGroup['topologies']?.length) {
                            this.topologies = dataGroup['topologies'];
                        }

                        this.members = this.formatServices(dataGroup['members'], WizardTitleEnum.MEMBERS);
                        this.patchTemplateToForm(template);
                        // Once everything is loaded check to see if there are any error in the controller state
                        if (serviceError?.error?.length) {
                            this.setErrorStateOnPage(serviceError.error);
                        }
                    },
                    error: (error: ErrorResponseModel) => {
                        this.logService.error(
                            'GlobalNamesComponent:initLoading',
                            'Unable to get global name from the API', error.message
                        );
                    }
                }
            );
    }

    /**
     * Initialises the form values.
     * @param template
     * @private
     */
    private patchTemplateToForm(template: any): void {
        // Setup dropdowns before patching values
        this.setUpDropdown();

        // Populate our patch
        const templatePatch = this.createTemplatePatch(template);

        // Check if we are in advanced mode and set the correct states on the inputs before patching
        this.advancedMode = this.wizardService.wizardMode;

        // Patch our hidden state
        this.patchTemplateHiddenState(template, this.inputStateStore);

        this.registerSubSteps(WizardTitleEnum.TOPOLOGIES);

        // Apply patch to form
        this.applyPatchAndValidate(templatePatch);

        // Tell the wizard to show the content
        this.wizardService.isLoading = false;

        // Once patched output the values to the controller state.
        this.outputFormValues();
    }

    /**
     * Set up the dropdown values.
     * @private
     */
    private setUpDropdown(): void {
        const members: any[] = [];
        if (this.members) {
            for (const member of this.members) {
                members.push(<DropdownModel>{ key: member.name, value: member.name });
            }
        }

        this.dropdown.members = members;
    }

    /**
     * Check for error state.
     * @param stepError
     * @private
     */
    private setErrorStateOnPage(stepError: any[]): void {
        this.loadedWithErrors(stepError);
    }

    /**
     * Format form values for output.
     * @private
     */
    private outputFormValues(): void {
        if (!this.wizardService.isLoading) {
            const outputObject = this.flattenServiceFormValues(<GslbFrontendModel>{});

            outputObject.member = this.getMemberId(outputObject);

            // loop through form values
            const template: TemplateModel = this.wizardService.controllerState[WizardTitleEnum.TOPOLOGIES][this.templateIndex];
            for (const property in outputObject) {
                if (outputObject.hasOwnProperty(property)) {
                    const visibility = this.inputStateStore[property]?.hidden;
                    template[property] = {
                        value: outputObject[property],
                        hidden: visibility !== undefined ? visibility : true
                    };
                }
            }
        }
    }

    /**
     * Get member Id.
     * @param outputObj
     * @private
     */
    private getMemberId(outputObj: any): string {
        if (!outputObj.member) {
            return null;
        } else {
            const match = this.members.find(item => item.name === outputObj.member);
            if (match) {
                return match.id;
            } else {
                this.logService.error('TopologyComponent: getMemberId', 'Unable to find a matching member id');

                return null;
            }
        }
    }
}
